import { useState, type FormEvent } from 'react';
import type { ProgramTheme, ProgramThemePayload } from '../api/useProgramThemes';
import { Section } from './DetailSection';
import { ImageUploadField, type UploadedImage } from './ImageUploadField';
import { ColorField, isHexColor } from './ColorField';
import { PassPreview } from './PassPreview';
import { Notice } from './Notice';

type ImageKey = 'icon' | 'logo' | 'strip' | 'hero';
type ColorKey = 'label_color' | 'text_color' | 'background_color';

interface FormValues {
  name: string;
  icon: UploadedImage | null;
  logo: UploadedImage | null;
  strip: UploadedImage | null;
  hero: UploadedImage | null;
  label_color: string;
  text_color: string;
  background_color: string;
}

const emptyValues: FormValues = {
  name: '',
  icon: null,
  logo: null,
  strip: null,
  hero: null,
  label_color: '',
  text_color: '',
  background_color: '',
};

const fromTheme = (theme: ProgramTheme): FormValues => ({
  name: theme.name,
  icon: { uri: theme.icon2x, derived: theme.icon },
  logo: { uri: theme.logo2x, derived: theme.logo },
  strip: { uri: theme.strip2x, derived: theme.strip },
  hero: theme.hero ? { uri: theme.hero, derived: theme.hero_preview ?? '' } : null,
  label_color: theme.label_color.toUpperCase(),
  text_color: theme.text_color.toUpperCase(),
  background_color: theme.background_color.toUpperCase(),
});

const toPayload = (v: FormValues): ProgramThemePayload => ({
  name: v.name.trim(),
  icon: v.icon!.derived,
  icon2x: v.icon!.uri,
  logo: v.logo!.derived,
  logo2x: v.logo!.uri,
  strip: v.strip!.derived,
  strip2x: v.strip!.uri,
  label_color: v.label_color,
  text_color: v.text_color,
  background_color: v.background_color,
  ...(v.hero && { hero: v.hero.uri, hero_preview: v.hero.derived }),
});

const changedFields = (next: ProgramThemePayload, prev: ProgramThemePayload) => {
  const changes: Partial<ProgramThemePayload> = {};
  for (const key of Object.keys(next) as (keyof ProgramThemePayload)[]) {
    const a = next[key];
    const b = prev[key];
    const same = key.endsWith('_color') ? a?.toLowerCase() === b?.toLowerCase() : a === b;
    if (!same) changes[key] = a;
  }
  return changes;
};

const validate = (v: FormValues) => {
  const errors: Partial<Record<'name' | ImageKey | ColorKey, string>> = {};
  if (!v.name.trim()) errors.name = 'Enter a theme name.';
  if (!v.icon) errors.icon = 'Upload an icon.';
  if (!v.logo) errors.logo = 'Upload a logo.';
  if (!v.strip) errors.strip = 'Upload a banner.';
  (['label_color', 'text_color', 'background_color'] as const).forEach((key) => {
    if (!isHexColor(v[key])) errors[key] = 'invalid';
  });
  return errors;
};

const passImages: { key: Exclude<ImageKey, 'hero'>; label: string; hint: string }[] = [
  { key: 'icon', label: 'Icon', hint: 'Shown in notifications and on the lock screen. Upload the 2x image; a 1x copy is made for you.' },
  { key: 'logo', label: 'Logo', hint: 'Top-left of the pass. Upload the 2x image; a 1x copy is made for you.' },
  { key: 'strip', label: 'Banner', hint: 'The strip across the pass. Upload the 2x image; a 1x copy is made for you.' },
];

const colors: { key: ColorKey; label: string; hint: string }[] = [
  { key: 'background_color', label: 'Background colour', hint: 'Fills the pass.' },
  { key: 'label_color', label: 'Label colour', hint: 'Small field labels, like "Member".' },
  { key: 'text_color', label: 'Text colour', hint: 'Field values, like the member name.' },
];

interface ThemeFormProps {
  theme?: ProgramTheme;
  submitLabel: string;
  isSaving: boolean;
  error?: string;
  success?: string;
  onSubmit: (payload: Partial<ProgramThemePayload>) => void;
}

export const ThemeForm = ({ theme, submitLabel, isSaving, error, success, onSubmit }: ThemeFormProps) => {
  const isEdit = theme !== undefined;
  const [initial] = useState(() => (theme ? fromTheme(theme) : emptyValues));
  const [values, setValues] = useState(initial);
  const [uploading, setUploading] = useState<Partial<Record<ImageKey, boolean>>>({});
  const [attempted, setAttempted] = useState(false);

  const errors = validate(values);
  const isValid = Object.keys(errors).length === 0;
  const isUploading = Object.values(uploading).some(Boolean);
  const changes = isEdit && isValid ? changedFields(toPayload(values), toPayload(initial)) : null;
  const hasChanges = !isEdit || (changes !== null && Object.keys(changes).length > 0);

  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) => setValues((v) => ({ ...v, [key]: value }));
  const setBusy = (key: ImageKey) => (busy: boolean) => setUploading((u) => ({ ...u, [key]: busy }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setAttempted(true);
    if (!isValid || isUploading || isSaving) return;
    onSubmit(isEdit ? changes ?? {} : toPayload(values));
  };

  const shownError = (key: keyof typeof errors) => (attempted ? errors[key] : undefined);

  return (
    <form onSubmit={handleSubmit} noValidate className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="flex min-w-0 flex-col gap-6">
        <Section title="Name">
          <div className="flex flex-col gap-1">
            <label htmlFor="theme-name" className="text-sm font-medium text-gray-700">
              Theme name
            </label>
            <p className="text-xs text-gray-500">Only ops sees this name.</p>
            <input
              id="theme-name"
              type="text"
              value={values.name}
              onChange={(e) => set('name', e.target.value)}
              aria-invalid={shownError('name') ? true : undefined}
              aria-describedby={shownError('name') ? 'theme-name-error' : undefined}
              className={`max-w-sm px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                shownError('name') ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {shownError('name') && (
              <p id="theme-name-error" className="text-xs text-red-600">
                {shownError('name')}
              </p>
            )}
          </div>
        </Section>

        <Section title="Pass images">
          <div className="flex flex-col gap-6">
            {passImages.map(({ key, label, hint }) => (
              <ImageUploadField
                key={key}
                id={`theme-${key}`}
                label={label}
                hint={hint}
                mode="pass"
                value={values[key]}
                onChange={(img) => set(key, img)}
                onBusyChange={setBusy(key)}
                error={shownError(key)}
                disabled={isSaving}
              />
            ))}
          </div>
        </Section>

        <Section title="Colours">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {colors.map(({ key, label, hint }) => (
              <ColorField
                key={key}
                id={`theme-${key}`}
                label={label}
                hint={hint}
                value={values[key]}
                onChange={(c) => set(key, c)}
                showError={attempted || values[key].length >= 6}
                disabled={isSaving}
              />
            ))}
          </div>
        </Section>

        <Section title="Hero image (optional)">
          <ImageUploadField
            id="theme-hero"
            label="Hero image"
            hint={
              isEdit && initial.hero
                ? "The program's hero image. A small preview is made for you. A saved hero can be replaced but not removed."
                : "The program's hero image. A small preview is made for you."
            }
            mode="hero"
            value={values.hero}
            onChange={(img) => set('hero', img)}
            onBusyChange={setBusy('hero')}
            removable={!(isEdit && initial.hero)}
            disabled={isSaving}
          />
        </Section>

        <div className="flex flex-col gap-3">
          {success && !error && <Notice tone="success">{success}</Notice>}
          {error && <Notice tone="error">{error}</Notice>}
          {attempted && !isValid && <Notice tone="error">Fix the highlighted fields to save this theme.</Notice>}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={isSaving || isUploading || !hasChanges}
              className="py-2 px-4 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : submitLabel}
            </button>
            {isUploading && <span className="text-sm text-gray-500">Waiting for uploads to finish…</span>}
            {!isUploading && isEdit && !hasChanges && isValid && <span className="text-sm text-gray-500">No changes yet.</span>}
          </div>
        </div>
      </div>

      <div className="lg:sticky lg:top-6 lg:self-start">
        <Section title="Pass preview">
          <PassPreview
            name={values.name}
            logo={values.logo?.uri}
            strip={values.strip?.uri}
            icon={values.icon?.uri}
            labelColor={values.label_color}
            textColor={values.text_color}
            backgroundColor={values.background_color}
          />
        </Section>
      </div>
    </form>
  );
};
